"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import DownArrow from "@/components/icons/down-arrow";
import Close from "@/components/icons/close";
import Menu from "@/components/icons/menu";
import { usePathname } from "next/navigation";
import Image from "next/image";
import AnimatedButton from "@/components/form/animated-button";
import { motion } from "framer-motion";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Call from "@/components/icons/call";
import Button from "./form/button";
import { cn } from "@/lib/utils";

const Header = ({ header, openLink, setOpenLink }) => {
  const path = usePathname();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileLink, setOpenMobileLink] = useState(null);
  const [activeHeading, setActiveHeading] = useState(null);
  const [expandedHeading, setExpandedHeading] = useState(null);
  const [expandedHeading2, setExpandedHeading2] = useState(null);
  const [expandedHeadings, setExpandedHeadings] = useState({});
  const [fontSizeClass, setFontSizeClass] = useState("text-base");
  const [showHeader, setShowHeader] = useState(true);
  const [submenuPosition, setSubmenuPosition] = React.useState({});

  const increaseFontSize = () => {
    if (fontSizeClass === "text-base") setFontSizeClass("text-lg");
    else if (fontSizeClass === "text-lg") setFontSizeClass("text-xl");
    else if (fontSizeClass === "text-xl") setFontSizeClass("text-2xl");
  };

  const decreaseFontSize = () => {
    if (fontSizeClass === "text-2xl") setFontSizeClass("text-xl");
    else if (fontSizeClass === "text-xl") setFontSizeClass("text-lg");
    else if (fontSizeClass === "text-lg") setFontSizeClass("text-base");
  };

  const resetFontSize = () => {
    setFontSizeClass("text-base");
  };
  const handleMenuClick = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = (linkName) => {
    if (linkName.subLinks) {
      setOpenLink(openLink === linkName.name ? null : linkName.name);
    } else {
      setOpenLink(null);
    }
  };

  const navRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenLink(null);
        setExpandedHeading(null);
        setMobileMenuOpen(false);
      }
    };
  }, []);

  useEffect(() => {
    document.body.classList.remove(
      "text-base",
      "text-lg",
      "text-xl",
      "text-2xl"
    );
    document.body.classList.add(fontSizeClass);
  }, [fontSizeClass]);

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: (i, number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 1,
        ease: "easeInOut",
      },
    }),
  };

  return (
    <header className="sticky z-40 top-0 text-gray-800 print:hidden">
      <motion.header
        initial="hidden"
        animate="visible"
        className="bg-white lg:top-[35px] w-full min-h-[8vh] flex justify-between items-center px-1 md:px-5 lg:px-1 xl:px-5  lg:rounded-t-lg"
      >
        <motion.div
          custom={0}
          variants={headerVariants}
          className="flex items-center"
        >
          <Link href='/'>
            <Image
              src={header.images.logo.src}
              alt={header.images.logo.alt}
              className="object-cover w-14 md:w-16 lg:w-[5vw] xl:w-[4.6vw] h-full mr-1.5 xl:mr-2.5"
              width={180}
              height={180}
              sizes="(max-width: 640px) 60px, (max-width: 768px) 70px, (max-width: 1280px) 10vw, 10vw"
              loading="eager"
            />
          </Link>
          <div className="mt-1">
            <p className="text-[#002C69] text-[10px] sm:text-[16px] md:text-lg lg:text-xs 2xl:text-xl font-extrabold">
              కుప్పం గ్రామీణ విద్యుత్ సహకార సంఘం లిమిటెడ్
            </p>
            <p className="text-red-600 text-[8px] sm:text-xs font-semibold md:text-xs lg:text-[10px] 2xl:text-base lg:font-semibold xl:font-bold lg:ml-0 xl:ml-3 2xl:ml-2">
              KUPPAM RURAL ELECTRIC CO-OPERATIVE SOCIETY LIMITED
            </p>
          </div>
        </motion.div>

        <motion.div custom={1} variants={headerVariants}>
          <Image
            className="object-cover w-14 lg:w-[5vw] h-auto lg:-ml-1 mr-4 lg:mr-1.5 2xl:mr-0"
            src={header.images.cm.src}
            alt={header.images.cm.alt}
            
            width={140}
            height={140}
            sizes="(max-width: 640px) 60px, (max-width: 768px) 70px, (max-width: 1280px) 10vw, 10vw"
            loading="eager"
          />
        </motion.div>

        <motion.div
          custom={2}
          variants={headerVariants}
          className="text-xs font-medium hidden lg:block"
        >
          <div className="flex">
            <Call className="lg:w-5 lg:h-5 xl:w-7 xl:h-7 mr-2" />
            <p className="text-red-600 mt-1">08570 - 255018 / 255028</p>
          </div>
          <div>
            <p className="text-[#001436]">
              Toll- Free{" "}
              <span className="text-red-600">:: 1800 425 505 505 / 1912</span>
            </p>
          </div>
          <div>
            <p className="text-[#001436]">
              GST-IN <span className="text-red-600">:: 37AABAT4654R1ZQ</span>
            </p>
          </div>
        </motion.div>

        <motion.div
          custom={3}
          variants={headerVariants}
          className="text-[#001436] text-xs font-medium hidden lg:block"
        >
          <p>service@kuppamresco.org</p>
          <p>kuppamresco@yahoo.com</p>
          <p>md@kuppamresco.org</p>
        </motion.div>
        <div className="flex">
          <motion.div
            custom={4}
            variants={headerVariants}
            className="hidden lg:flex shadow-md rounded-lg mr-3 w-full h-full lg:mt-1"
          >
            <button
              onClick={decreaseFontSize}
              className="bg-white py-1.5 rounded-r-none text-green-1 border border-y-0 border-l-0 border-r-green-dark  text-sm xl:text-base font-medium px-2 2xl:px-3"
            >
              A-
            </button>
            <button
              onClick={increaseFontSize}
              className="bg-white py-1.5 rounded-none text-green-1 border border-y-0 border-l-0 border-r-green-dark text-sm xl:text-base font-medium px-2 2xl:px-3"
            >
              A+
            </button>
            <button
              className="bg-white py-1.5 rounded-l-none text-green-1  text-sm xl:text-base px-2 2xl:px-3 xl:mr-2 font-bold"
              onClick={() => {
                window.location.reload();
                resetFontSize;
              }}
            >
              ⟳
            </button>
          </motion.div>
          <section className="hidden lg:block">
            <LanguageSwitcher />
          </section>
        </div>
      </motion.header>
      <nav className="bg-green-1 flex justify-between px-1 sm:py-1 md:px-5 lg:px-1 xl:px-5">
        <motion.nav
          custom={2}
          variants={headerVariants}
          ref={navRef}
          className="hidden lg:flex lg:items-center gap-x-1 xl:gap-x-5"
        >
          {header.links.map((link, i) => (
            <div
              key={i}
              className="relative"
              onMouseEnter={() => setOpenLink(link.name)}
              onMouseLeave={() => {
                setOpenLink(null);
                setExpandedHeading(null);
              }}
            >
              <div
                className={`!ml-0 px-1 rounded-md py-1.5 text-white ${path === link.href ||
                  (link.subLinks &&
                    link.subLinks.some((subLink) =>
                      path.startsWith(subLink.url)
                    ))
                  ? "text-white"
                  : ""
                  }`}
              >
                {link.subLinks ? (
                  <motion.button
                    custom={2 + i}
                    variants={headerVariants}
                    className="flex items-center relative"
                    type="button"
                  >
                    <span
                      className={cn(
                        fontSizeClass === "text-base"
                          ? ""
                          : "text-inherit",
                        openLink === link.name
                          ? "text-white font-semibold"
                          : "font-semibold"
                      )}
                    >
                      {link.name}
                    </span>
                    <DownArrow
                      className={`size-4 mt-1 ml-1 ${openLink === link.name ? "text-white" : "rotate-180"
                        }`}
                    />
                    <span
                      className={`absolute left-0 bottom-0 w-0 h-0.5 bg-white transition-all duration-1000 ease-in-out hover:w-full hover:h-[2px] ${link.subLinks &&
                        link.subLinks.some((subLink) =>
                          path.startsWith(subLink.url)
                        )
                        ? "w-full h-[2px]"
                        : ""
                        }`}
                    />
                  </motion.button>
                ) : (
                  <Link
                    href={link.href}
                    onClick={() => handleLinkClick(link)}
                    className="flex items-center hover:text-white relative"
                  >
                    <span
                      className={`${openLink === link.name
                        ? "text-white font-semibold"
                        : "font-semibold"
                        }`}
                    >
                      {link.name}
                    </span>
                    <span
                      className={`absolute left-0 bottom-0 w-0 h-0.5 bg-white transition-all duration-1000 ease-in-out hover:w-full hover:h-[2px] ${path === link.href ? "w-full h-[2px]" : ""
                        }`}
                    />
                  </Link>
                )}
              </div>

              <div className="relative opacity-90">
                {openLink === link.name &&
                  [1, 2, 3, 4, 5, 6, 7].some(
                    (n) => link[`subLinks${n}`] || (n === 1 && link.subLinks)
                  ) && (
                    <div className="absolute -left-4 top-full bg-white shadow-lg rounded-lg text-nowrap w-fit max-h-[600px] z-40 px-4 py-3">
                      <div className="flex flex-col gap-2">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
                          const subLinks =
                            link[`subLinks${n}`] || (n === 1 && link.subLinks);
                          const heading =
                            link[`heading${n}`] || (n === 1 && link.heading);

                          if (!subLinks) return null;
                          return (
                            <div
                              key={heading || n}
                              className="relative group opacity-90"
                              onMouseEnter={(e) => {
                                setExpandedHeading(heading);
                                // setActiveHeading(heading);
                                // calculate submenu position
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                const spaceRight =
                                  window.innerWidth - rect.right;
                                const submenuWidth = 300; // approximate submenu width in px, adjust as needed

                                if (spaceRight < submenuWidth) {
                                  setSubmenuPosition((prev) => ({
                                    ...prev,
                                    [heading]: "left",
                                  }));
                                } else {
                                  setSubmenuPosition((prev) => ({
                                    ...prev,
                                    [heading]: "right",
                                  }));
                                }
                              }}
                            >
                              {heading ? (
                                <>
                                  <button
                                    aria-label="down-arrow button"
                                    className={`flex justify-between items-center w-full text-left py-1 font-semibold ${activeHeading === heading
                                      ? "bg-green-1 text-white text-lg duration-500 rounded-md"
                                      : "text-gray-800 hover:text-green-1"
                                      }`}
                                  >
                                    {heading}
                                    <span className="transition-transform ml-3 rotate-90">
                                      <DownArrow />
                                    </span>
                                  </button>

                                  {/* Submenu card positioned beside the heading */}
                                  {expandedHeading === heading && (
                                    <div
                                      className={`absolute top-0 bg-white shadow-2xl rounded-lg p-4 z-50 w-fit ${expandedHeading2
                                        ? ""
                                        : "max-h-[500px] overflow-y-auto"
                                        }
                                        ${submenuPosition[heading] === "left"
                                          ? "right-full mr-4"
                                          : "left-full ml-4"
                                        }
                                      `}
                                    >
                                      <ul className="space-y-2 relative">
                                        {subLinks.map((subLink, index) => (
                                          <li
                                            key={index}
                                            className="relative group"
                                          >
                                            {subLink?.heading ? (
                                              <>
                                                <button
                                                  aria-label="down-arrow button"
                                                  onMouseEnter={() =>
                                                    setExpandedHeading2(
                                                      subLink.heading
                                                    )
                                                  }
                                                  onMouseLeave={() =>
                                                    setExpandedHeading2(null)
                                                  }
                                                  className={`flex justify-between items-center w-full text-left py-1 font-medium ${path === subLink.heading
                                                    ? "bg-green-1 text-white text-lg duration-500"
                                                    : "text-gray-800 hover:text-green-1"
                                                    }`}
                                                >
                                                  {subLink.heading}
                                                  <span className="transition-transform ml-3 rotate-90">
                                                    <DownArrow />
                                                  </span>
                                                </button>

                                                {subLink.heading ===
                                                  expandedHeading2 &&
                                                  subLink.subLinks && (
                                                    <ul
                                                      onMouseEnter={() =>
                                                        setExpandedHeading2(
                                                          subLink.heading
                                                        )
                                                      }
                                                      onMouseLeave={() =>
                                                        setExpandedHeading2(
                                                          null
                                                        )
                                                      }
                                                      className="absolute left-full top-0 z-20 min-w-[220px] bg-white shadow-lg rounded-lg p-2 space-y-1"
                                                    >
                                                      {subLink.subLinks.map(
                                                        (
                                                          childLink,
                                                          childIndex
                                                        ) => (
                                                          <li key={childIndex}>
                                                            <Link
                                                              href={
                                                                childLink.url
                                                              }
                                                              target="_blank"
                                                              className={`block px-2 py-1 text-sm xl:text-base font-medium  ${path ===
                                                                childLink.url
                                                                ? "bg-green-1 text-white text-lg duration-500"
                                                                : "text-gray-800 hover:text-green-1"
                                                                }`}
                                                            >
                                                              {childLink.name}
                                                            </Link>
                                                          </li>
                                                        )
                                                      )}
                                                    </ul>
                                                  )}
                                              </>
                                            ) : (
                                              <Link
                                                href={subLink.url}
                                                // subLink.url ===
                                                //     "https://pgi.billdesk.com/pgidsk/pgmerc/kresco/KRESCODetails.jsp"
                                                target={
                                                
                                                    subLink.url === "https://kuppamresco.org/ConsumerDashboard/serviceDetails.jsp" 
                                                    || subLink.url.includes("/pdfs")
                                                    ? "_blank"
                                                    : undefined
                                                }
                                                className={`block py-1 text-sm xl:text-base font-medium  ${path === subLink.url
                                                  ? "bg-green-1 text-white text-lg duration-500"
                                                  : "text-gray-800 hover:text-green-1"
                                                  }`}
                                              >
                                                {subLink.name}
                                              </Link>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <ul className="mt-1 space-y-1">
                                  {subLinks.map((subLink, index) => (
                                    <li key={index}>
                                      <Link
                                        href={subLink.url}
                                        target={
                                          subLink.url === "/energy-calculator" ||  subLink.url.includes("/pdfs")
                                            ? "_blank"
                                            // : subLink.url ===
                                            //   "/pdfs/rti-act.pdf"
                                            //   ? "_blank"
                                            //   : subLink.url ===
                                            //     "/pdfs/rti-telugu.pdf" || subLink.url ===
                                            //     "/pdfs/exemption.pdf"
                                            //     ? "_blank"

                                                : undefined
                                        }
                                        onClick={() => {
                                          setOpenLink(null);
                                          setExpandedHeading(null);
                                        }}
                                        className={`block py-1 text-sm xl:text-base font-semibold ${path === subLink.url
                                          ? "bg-green-1 text-white text-lg duration-500"
                                          : "text-gray-800 hover:text-green-1"
                                          }`}
                                      >
                                        {subLink.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          ))}
        </motion.nav>
        <motion.div
          custom={10}
          variants={headerVariants}
          className="flex w-full lg:w-fit justify-end gap-4 lg:gap-0 px-2 lg:px-0"
        >
          <LanguageSwitcher className="lg:hidden" />
          <button
            aria-label="menu open-close button"
            className="lg:hidden"
            onClick={handleMenuClick}
          >
            {isMobileMenuOpen ? (
              <Close className="h-8 text-white hover:motion" />
            ) : (
              <Menu className="h-8 text-white hover:motion" />
            )}
          </button>
          <AnimatedButton
            label={header.content.login}
            className="mt-0.5 lg:mt-"
          />
        </motion.div>
      </nav>

      {isMobileMenuOpen && (
        <div
          ref={navRef}
          className="bg-green-1 absolute top-[105%] w-full lg:hidden translate-y-0 duration-1000 z-50 rounded-lg max-h-96 overflow-y-auto"
        >
          <div className="text-base font-medium flex flex-col items-center px-4 py-1 text-white">
            {header.links.map((link, i) => (
              <div key={i} className="w-full">
                {link.href &&
                  !Object.keys(link).some(
                    (key) =>
                      key.startsWith("subLinks") && Array.isArray(link[key])
                  ) ? (
                  <Link
                    href={link.href}
                    className="flex justify-between items-center w-full text-lg font-semibold py-1.5 hover:bg-gray-300 rounded-md px-1 text-left"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setOpenMobileLink(null);
                    }}
                  >
                    <span>{link.name}</span>
                  </Link>
                ) : (
                  <button
                    aria-label="down arrow button"
                    className="flex justify-between items-center w-full text-lg font-semibold py-1.5 hover:bg-gray-300 hover:text-green-1 rounded-md px-1 text-left"
                    onClick={() =>
                      setOpenMobileLink(
                        openMobileLink === link.name ? null : link.name
                      )
                    }
                  >
                    <span>{link.name}</span>
                    {Object.keys(link).some(
                      (key) =>
                        key.startsWith("subLinks") && Array.isArray(link[key])
                    ) && (
                        <DownArrow
                          className={`size-4 transition-transform ${openMobileLink === link.name ? "" : "rotate-180"
                            }`}
                        />
                      )}
                  </button>
                )}

                {openMobileLink === link.name && (
                  <div className="w-full bg-green-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                      const subLinks =
                        link[`subLinks${n}`] || (n === 1 && link.subLinks);
                      const heading =
                        link[`heading${n}`] || (n === 1 && link.heading);

                      if (!subLinks) return null;

                      const isExpanded =
                        expandedHeadings[link.name] === heading;

                      return (
                        <div key={n} className="px-4 py-2">
                          {heading && (
                            <button
                              aria-label="down arrow button"
                              onClick={() =>
                                setExpandedHeadings((prev) => ({
                                  ...prev,
                                  [link.name]: isExpanded ? null : heading,
                                }))
                              }
                              className={`w-full flex justify-between font-semibold py-2 px-2 rounded-md hover:bg-gray-300 hover:text-green-1 duration-300 ${activeHeading === heading
                                ? "bg-gray-300 text-green-1"
                                : "text-white"
                                }`}
                            >
                              {heading}
                              <DownArrow
                                className={`ml-2 w-5 h-5 inline-block transition-transform ${isExpanded ? "" : "rotate-180"
                                  }`}
                              />
                            </button>
                          )}

                          {(isExpanded || !heading) && (
                            <ul className="mt-1">
                              {subLinks.map((subLink, index) => {
                                if (subLink.subLinks) {
                                  const nestedKey = `${link.name} > ${heading} > ${subLink.heading}`;
                                  const isChildExpanded =
                                    expandedHeadings[nestedKey];

                                  return (
                                    <li key={index} className="pl-2">
                                      <button
                                        aria-label="down arrow button"
                                        onClick={() =>
                                          setExpandedHeadings((prev) => ({
                                            ...prev,
                                            [nestedKey]: isChildExpanded
                                              ? null
                                              : true,
                                          }))
                                        }
                                        className={`w-full flex justify-between font-medium py-2 px-2 rounded-md hover:bg-gray-300 hover:text-green-1 duration-300 ${activeHeading === subLink.heading
                                          ? "bg-gray-300 text-green-1"
                                          : "text-white"
                                          }`}
                                      >
                                        {subLink.heading}
                                        <DownArrow
                                          className={`ml-2 w-4 h-4 transition-transform ${isChildExpanded ? "" : "rotate-180"
                                            }`}
                                        />
                                      </button>

                                      {isChildExpanded && (
                                        <ul className="mt-1 pl-2">
                                          {subLink.subLinks.map(
                                            (nestedLink, nestedIndex) => (
                                              <li key={nestedIndex}>
                                                <Link
                                                  href={nestedLink.url}
                                                  onClick={() => {
                                                    setMobileMenuOpen(false);
                                                    setOpenMobileLink(null);
                                                    setExpandedHeadings({});
                                                    setActiveHeading(
                                                      subLink.heading
                                                    );
                                                  }}
                                                  className={`block px-4 py-2 text-sm font-medium rounded-md hover:bg-[#f3dada] duration-300 ${path === nestedLink.url
                                                    ? "bg-gray-300 text-gray-700"
                                                    : "text-white"
                                                    }`}
                                                >
                                                  {nestedLink.name}
                                                </Link>
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      )}
                                    </li>
                                  );
                                }

                                // Direct sublink without children
                                return (
                                  <li key={index}>
                                    <Link
                                      href={subLink.url}
                                      onClick={() => {
                                        setMobileMenuOpen(false);
                                        setOpenMobileLink(null);
                                        setExpandedHeadings({});
                                        setActiveHeading(heading);
                                      }}
                                      className={`block px-4 py-2 text-sm font-medium rounded-md hover:bg-[#f3dada] duration-300 ${path === subLink.url
                                        ? "bg-gray-300 text-gray-700"
                                        : "text-white"
                                        }`}
                                    >
                                      {subLink.name}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
export default Header;
